import * as React from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";

interface Props {
    open: boolean;
    onClose: () => void;
    title: React.ReactNode;
    subtitle?: React.ReactNode;
    children: React.ReactNode;
    dataTestid?: string;
    widthClass?: string;
}

/**
 * Right-side slide-out drawer used for entity detail views (leads, visits).
 * Closes on backdrop click or the explicit Close button.
 */
export function SideDrawer({
    open,
    onClose,
    title,
    subtitle,
    children,
    dataTestid,
    widthClass = "max-w-xl",
}: Props): React.ReactNode {
    React.useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent): void => {
            if (e.key === "Escape") onClose();
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    if (!open) return null;
    return (
        <div
            className="fixed inset-0 z-40 flex items-end justify-end bg-background/60 backdrop-blur-sm"
            onClick={onClose}
        >
            <div
                data-testid={dataTestid}
                className={`flex h-full w-full ${widthClass} flex-col border-l bg-background shadow-xl`}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-start justify-between gap-3 border-b px-6 py-4">
                    <div className="min-w-0">
                        <h2 className="text-sm font-semibold">{title}</h2>
                        {subtitle && (
                            <p className="mt-0.5 truncate text-xs text-muted-foreground">{subtitle}</p>
                        )}
                    </div>
                    <Button variant="ghost" size="sm" onClick={onClose} aria-label="Close">
                        <X className="h-4 w-4" />
                    </Button>
                </div>
                <div className="flex-1 overflow-y-auto p-6 text-sm">{children}</div>
            </div>
        </div>
    );
}

/** Small reusable section header + body pair used in the drawer body. */
export function DrawerSection({
    title,
    children,
}: {
    title: string;
    children: React.ReactNode;
}): React.ReactNode {
    return (
        <div className="mb-6 last:mb-0">
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {title}
            </h3>
            <div className="space-y-1.5">{children}</div>
        </div>
    );
}

/** Row of label + value (value rendered monospace so ids/cookies line up). */
export function DrawerRow({
    label,
    value,
    mono = true,
}: {
    label: string;
    value: React.ReactNode;
    mono?: boolean;
}): React.ReactNode {
    return (
        <div className="grid grid-cols-[120px,1fr] items-start gap-3">
            <span className="text-xs text-muted-foreground">{label}</span>
            <span className={`break-all text-xs ${mono ? "font-mono" : ""}`}>{value}</span>
        </div>
    );
}
